/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useMemo} from "react"
import {useTranslation} from "react-i18next"
import cx from "classnames"
import {Icon} from "components/Icon"
import styles from "./StatsHeaderInfo.module.css"
import {statsService} from "services"
import {handleError} from "helpers"
import {useQuery} from "@tanstack/react-query"
import {BaseLoading} from "components"
import {Tabs} from "antd"
import TabPane from "antd/lib/tabs/TabPane"
import {TabHeader} from "components/Tab"
import moment from "moment"
import {useAllCampuses, useModel} from "hooks"

export function StatsHeaderInfo() {
    const {t} = useTranslation(["common"])
    const [activeTabKey, onChangeTab] = useState<string>("all")
    const model = useModel()
    const isAdmin = model.isAdmin()
    const {campuses: allCampuses} = useAllCampuses()
    const campusIds = useMemo(() => {
        const _ids = new Set(
            isAdmin
                ? (allCampuses || []).map((campus) => campus.id)
                : (model.user.profiles || [])
                      .map((profile) => profile.campuses || [])
                      .flat()
                      .map((campus) => campus.id)
        )
        return Array.from(_ids)
    }, [isAdmin, model.user.campuses, allCampuses])

    const statQuery = useQuery({
        queryKey: ["department-stats-admissions", campusIds],
        queryFn: () => statsService.getDepartmentStatsAdmissions({campusIds}),
        onError: handleError,
        placeholderData: []
    })

    const allCampusStat = useMemo(() => {
        const startDate = (statQuery.data ?? []).reduce((latest: string | undefined, curr) => {
            const currDate = moment.utc(curr?.startDate)
            if (!currDate.isValid()) return latest
            if (!latest) return currDate.format("YYYY-MM-DD")
            return currDate.isBefore(moment.utc(latest)) ? currDate.format("YYYY-MM-DD") : latest
        }, undefined as string | undefined)
        let allStat = (statQuery.data ?? [])
            .filter((item) => item.startDate === startDate)
            .reduce(
                (acc, curr) => {
                    acc.prospects += curr?.prospects ?? 0
                    acc.applicants += curr?.applicants ?? 0
                    acc.enrolled += curr?.enrolled ?? 0
                    acc.seats += curr?.seats ?? 0
                    return acc
                },
                {prospects: 0, applicants: 0, enrolled: 0, seats: 0}
            ) ?? {prospects: 0, applicants: 0, enrolled: 0, seats: 0}

        allStat.startDate = startDate
        return allStat
    }, [statQuery.data])

    const orderedCampusStats = useMemo(() => {
        const data = [...(statQuery.data ?? [])]
        data.sort((a, b) => {
            const aDate = moment.utc(a?.startDate)
            const bDate = moment.utc(b?.startDate)
            const aTs = aDate.isValid() ? aDate.valueOf() : Number.NEGATIVE_INFINITY
            const bTs = bDate.isValid() ? bDate.valueOf() : Number.NEGATIVE_INFINITY
            return aTs - bTs
        })
        return data
    }, [statQuery.data])

    const renderTab = (campusId?: number) => {
        const campusData = campusId ? statQuery.data?.find((item) => item.campusId === campusId) : allCampusStat
        const todayDayOfYear = moment().startOf("date").utc()
        const startDateDayOfYear = moment.utc(campusData?.startDate)
        const nextAdmissionDays = startDateDayOfYear.diff(todayDayOfYear, "days")

        return (
            <div className={styles.headerInfoWrap}>
                <div className={styles.headerInfoLeft}>
                    <div className={styles.headerInfoLeftItem}>
                        <p className={styles.headerInfoLeft__title}>{t("staffHeader.nextAdmissionIn")}</p>
                        <p className={styles.headerInfoLeft__day}>
                            {nextAdmissionDays === 0
                                ? t("staffHeader.noUpcomingDates")
                                : t("staffHeader.nDays", {count: nextAdmissionDays})}
                        </p>
                    </div>
                    <div className={styles.headerInfoLeftItem}>
                        <p className={styles.headerInfoLeft__title}>{t("staffHeader.starts")}</p>
                        <div className={styles.headerInfoLeft__dateValueWrap}>
                            <p className={styles.headerInfoLeft__dateValue}>{campusData.startDate}</p>
                            <div className={styles.headerInfoLeft__dateIconWrap}>
                                <Icon
                                    icon="CALENDAR_FILL_INPUT"
                                    className={styles.headerInfoLeft__dateIcon}
                                    color="#fff"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.headerInfoRightItems}>
                    <div className={cx(styles.headerInfoRightItem, styles.headerInfoRightItemTarget)}>
                        <p className={styles.headerInfoRightItem__title}>SEATS</p>
                        <p className={styles.headerInfoRightItem__value}>{campusData.seats}</p>
                    </div>
                    <div className={cx(styles.headerInfoRightItem, styles.headerInfoRightItemSeats)}>
                        <p className={styles.headerInfoRightItem__title}>PROSPECTS</p>
                        <p className={styles.headerInfoRightItem__value}>{campusData.prospects}</p>
                    </div>
                    <div className={cx(styles.headerInfoRightItem, styles.headerInfoRightItemSeats)}>
                        <p className={styles.headerInfoRightItem__title}>APPLICANTS</p>
                        <p className={styles.headerInfoRightItem__value}>{campusData.applicants}</p>
                    </div>
                    <div className={styles.headerInfoRightItem}>
                        <p className={styles.headerInfoRightItem__title}>{t("staffHeader.enroll")}</p>
                        <div className={styles.headerInfoRightItemValueWrap}>
                            <Icon icon="POINTS_BIG" className={styles.headerInfoRightItem__icon} />
                            <p className={styles.headerInfoRightItem__value}>{campusData.enrolled}</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.container}>
            <Tabs className="fullwidth klassSubTab" activeKey={activeTabKey} onChange={onChangeTab}>
                <TabPane tab={<TabHeader title="All campuses" />} key="all">
                    {renderTab()}
                </TabPane>
                {orderedCampusStats.map((item) => (
                    <TabPane
                        tab={
                            <TabHeader
                                title={item.campusName}
                                indicator={
                                    moment.utc(item.startDate).format("YYYY-MM-DD") ===
                                    moment.utc(allCampusStat.startDate).format("YYYY-MM-DD") ? (
                                        <Icon icon="CHECKMARK_CIRCLE" className={styles.alertIcon} />
                                    ) : undefined
                                }
                            />
                        }
                        key={item.campusId}>
                        {renderTab(item.campusId)}
                    </TabPane>
                ))}
            </Tabs>
            <BaseLoading isShow={statQuery.isFetching} />
        </div>
    )
}
